// src/handlers/audienceHandlers.ts

import { message } from "antd";
import {
  deleteCustomList,
  updateCustomListStatus,
  bulkDeleteCustomLists,
  bulkUpdateCustomListStatus,
} from "src/services/AudienceListAPIs";
import { CustomList } from "src/layouts/Engagements/EngagementInterfaces";

export const getTitle = (
  actionType: "archive" | "unarchive" | "delete" | null,
  selectedItem: string | null
) => {
  const base = selectedItem ? "List/Segment" : "Lists/Segments";
  switch (actionType) {
    case "delete":
      return `Delete ${base}`;
    case "archive":
      return `Archive ${base}`;
    case "unarchive":
      return `Unarchive ${base}`;
    default:
      return "";
  }
};

export const getDescription = (
  actionType: "archive" | "unarchive" | "delete" | null,
  selectedItem: string | null,
  selectedLists: number[]
) => {
  if (selectedItem) {
    return `Are you sure you want to ${actionType} the list/segment "${selectedItem}"?`;
  }
  return `Are you sure you want to ${actionType} ${selectedLists.length} selected list(s)/segment(s)?`;
};

export const getIcon = (
  actionType: "archive" | "unarchive" | "delete" | null
) => {
  switch (actionType) {
    case "delete":
      return "/Icons/delete-icon.svg";
    case "archive":
      return "/Icons/archive-icon.svg";
    case "unarchive":
      return "/Icons/unarchive-icon.svg";
    default:
      return "";
  }
};

export const handleArchiveList = (
  list: CustomList,
  setSelectedItem: (v: string) => void,
  setSelectedListId: (v: number) => void,
  setActionType: (v: "archive" | "unarchive") => void,
  setModalOpen: (v: boolean) => void
) => {
  setSelectedItem(list.list_name);
  setSelectedListId(list.custom_list_id);
  setActionType(list.status === "archive" ? "unarchive" : "archive");
  setModalOpen(true);
};

export const handleDeleteClick = (
  listId: number,
  name: string,
  setSelectedItem: (v: string) => void,
  setSelectedListId: (v: number) => void,
  setActionType: (v: "delete") => void,
  setModalOpen: (v: boolean) => void
) => {
  setSelectedItem(name);
  setSelectedListId(listId);
  setActionType("delete");
  setModalOpen(true);
};

export const handleConfirm = async (
  selectedListId: number,
  actionType: "archive" | "unarchive",
  selectedItem: string | null,
  fetchCustomLists: () => void,
  setActionLoading: (v: boolean) => void,
  setModalOpen: (v: boolean) => void,
  resetState: () => void
) => {
  setActionLoading(true);

  try {
    const resolvedActionType =
      actionType === "unarchive" ? "active" : actionType;
    const data = await updateCustomListStatus(
      selectedListId,
      resolvedActionType
    );

    if (data.status === "success") {
      message.success(
        `List "${selectedItem}" ${
          actionType === "archive" ? "archived" : "unarchived"
        } successfully`
      );
      fetchCustomLists();
    } else {
      message.error(`Failed to ${actionType} list`);
    }
  } catch (err) {
    console.error(`Error ${actionType}ing list:`, err);
    message.error(`Error ${actionType}ing list`);
  } finally {
    setActionLoading(false);
    setModalOpen(false);
    resetState();
  }
};

export const handleDeleteConfirm = async (
  selectedListId: number,
  selectedItem: string | null,
  setRemoveLoading: (v: boolean) => void,
  setModalOpen: (v: boolean) => void,
  setData: React.Dispatch<React.SetStateAction<CustomList[]>>,
  resetState: () => void
) => {
  setRemoveLoading(true);
  try {
    const data = await deleteCustomList(selectedListId);

    if (data.status === "success") {
      message.success(`${selectedItem} list/segment deleted successfully`);
      setData(prev =>
        prev.filter(item => item.custom_list_id !== selectedListId)
      );
    } else {
      message.error("Failed to delete list/segment");
    }
  } catch (err) {
    // eslint-disable-next-line no-console -- intentional error logging
    console.error("Error deleting list:", err);
    message.error("Error deleting list/segment");
  } finally {
    setRemoveLoading(false);
    setModalOpen(false);
    resetState();
  }
};

export const handleBulkActionConfirm = async (
  selectedLists: number[],
  actionType: "archive" | "unarchive" | "delete",
  fetchCustomLists: () => Promise<void>,
  setLoadingStates: (loading: boolean) => void
) => {
  try {
    const tenantId = localStorage.getItem("tenant_id")!;
    let response;

    if (actionType === "delete") {
      response = await bulkDeleteCustomLists(tenantId, selectedLists);
    } else {
      const status = actionType === "archive" ? "archive" : "active";
      response = await bulkUpdateCustomListStatus(
        tenantId,
        selectedLists,
        status
      );
    }

    if (response.status === "success") {
      message.success(
        `${selectedLists.length} list(s) ${actionType}d successfully`
      );
      await fetchCustomLists();
    } else {
      message.error(`Failed to ${actionType} selected lists`);
    }
  } catch (error) {
    console.error(error);
    message.error(`Error ${actionType}ing selected lists`);
  } finally {
    setLoadingStates(false);
  }
};
